import { isDefined } from "@clipboard-health/util-ts";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import { useNavigationMenu } from "@src/appV2/redesign/AppLayout/Mobile/NavigationMenuContext";
import { HeaderMenuButton } from "@src/appV2/redesign/components/HeaderMenuButton/HeaderMenuButton";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { MaterialUiProviders } from "@src/mui/providers/MaterialUiProviders";
import React from "react";

export interface LegacyRouteWrapperProps {
  children: React.ReactNode;
  rootSx?: SystemStyleObject<Theme>;
  /**
   * Whether to render the fallback hamburger row above the content. Defaults to
   * `true`. Pages that render their own inline `HeaderMenuButton` (e.g. Berlin
   * redesign pages with a clean title) pass `false` to avoid a double button.
   */
  renderHeaderMenuButton?: boolean;
}

export function LegacyRouteWrapper(props: LegacyRouteWrapperProps) {
  const { children, rootSx, renderHeaderMenuButton = true } = props;
  // The navigation menu provider only mounts inside the mobile app shell, which
  // is pinned by session — not viewport width — for HH native sessions. Anything
  // below that is coupled to the shell gates on the provider, not a breakpoint.
  const isInMobileAppShell = isDefined(useNavigationMenu());
  // Legacy pages render in the legacy MUI theme (inside MaterialUiProviders) and
  // have no shared header to host the inline hamburger. Render it once here, above
  // the scroll area and re-wrapped in the Berlin theme so it styles correctly. The
  // row only mounts inside the mobile app shell (whose provider hosts the drawer
  // the button opens), so desktop is unaffected.
  const showHeaderMenuButton = isInMobileAppShell && renderHeaderMenuButton;

  const rootContainerSx: SxProps<Theme> = [
    { display: "flex", flexDirection: "column", flex: 1 },
    ...(isDefined(rootSx) ? [rootSx] : []),
  ];

  return (
    <MaterialUiProviders>
      <Box sx={rootContainerSx}>
        {showHeaderMenuButton && (
          <BerlinThemeProvider>
            <HeaderMenuButton sx={{ marginTop: 3, marginX: 3 }} />
          </BerlinThemeProvider>
        )}
        <Box
          sx={(theme) => ({
            padding: 3,
            flex: 1,
            minHeight: 0,
            // In the mobile app shell, the parent PageWrapperMobile sets
            // overflow:hidden on direct children to support touch gestures/bottom
            // sheets. This inner Box is not targeted by that selector, so it must
            // create the scroll context — at any viewport width, since the shell
            // can host wide webviews (HH native sessions on unfolded foldables).
            ...(isInMobileAppShell && { overflow: "auto" }),
            // On mobile/tablet, add padding to account for fixed floating navigation
            [theme.breakpoints.down("lg")]: {
              ...(theme.size?.floatingNavigation && {
                paddingBottom: `calc(${theme.size.floatingNavigation.height} + ${
                  theme.size.floatingNavigation.margin
                } + ${theme.spacing(3)})`,
              }),
            },
          })}
        >
          {children}
        </Box>
      </Box>
    </MaterialUiProviders>
  );
}
